/** How the page and the worker came to run the same version, if they did. */
export enum ServiceWorkerAlignment {
  /** No worker (unsupported, first visit, development without it): nothing to align. */
  noWorker = 'no-worker',
  /** The active worker already runs the page's version. */
  aligned = 'aligned',
  /** A newer worker took over; the tabs of the previous version reload themselves. */
  activatedNewWorker = 'activated-new-worker',
  /** The worker could not be brought to the page's version in time. */
  failed = 'failed',
}

/** Where the alignment of the worker with the page was, which tells what it waited for when it failed. */
export enum AlignmentStage {
  /** A newer worker had not finished installing. */
  installing = 'installing',
  /** A newer worker was told to take over (`SKIP_WAITING`) and did not become active. */
  takingOver = 'taking-over',
  /** The active worker's version was being read (or was read and is not the page's). */
  readingVersion = 'reading-version',
  /** The server was asked for a newer worker. */
  checkingForUpdate = 'checking-for-update',
}

/** Where an alignment that failed stood, and what it saw. */
export interface AlignmentFailure {
  stage: AlignmentStage;
  /**
   * The version the active worker reported: `null` when it did not answer, `undefined` when it was
   * not asked. Another version than the page's, with the server having no newer worker, is an old deploy.
   */
  activeVersion?: string | null;
  error?: unknown;
  /**
   * For a failure while a worker was told to take over: how long its activation was waited for, and the
   * state it was left in (`redundant`: a newer deploy replaced it, which is not a stuck worker).
   */
  takeOver?: { waitedMs: number; workerState: ServiceWorkerState };
}

/** One try to align, for the caller to decide what follows and to report. */
export interface AlignmentAttempt {
  alignment: ServiceWorkerAlignment;
  durationMs: number;
  failure?: AlignmentFailure;
}
